import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, X } from "lucide-react";

const DISMISS_KEY = "bookings_open_banner_dismissed";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Public-site banner shown from the auto-block unlock day through the last day
 * of the current month, announcing that next month's bookings are open.
 *
 * Hidden when:
 *  - auto-block is disabled
 *  - today is before the unlock day
 *  - any AUTO_BLOCK rows for next month still exist (i.e., not actually open yet)
 */
const BookingsOpenBanner = () => {
  const [show, setShow] = useState(false);
  const [monthLabel, setMonthLabel] = useState("");
  const [dismissKey, setDismissKey] = useState("");
  const navigate = useNavigate();

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dismissKey) localStorage.setItem(DISMISS_KEY, dismissKey);
    setShow(false);
  };

  const handleNavigate = () => {
    navigate("/book");
  };

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const today = new Date();
        const day = today.getDate();
        const month = today.getMonth();
        const year = today.getFullYear();

        const nextMonth = month === 11 ? 0 : month + 1;
        const nextMonthYear = month === 11 ? year + 1 : year;

        const pad = (n: number) => String(n).padStart(2, "0");
        const firstOfNext = `${nextMonthYear}-${pad(nextMonth + 1)}-01`;
        const daysInNextMonth = new Date(nextMonthYear, nextMonth + 1, 0).getDate();
        const lastOfNext = `${nextMonthYear}-${pad(nextMonth + 1)}-${pad(daysInNextMonth)}`;

        const { data: settings } = await supabase
          .from("site_settings")
          .select("key,value")
          .in("key", ["auto_block_unlock_day", "auto_block_enabled"]);

        const map = new Map((settings ?? []).map((r: any) => [r.key, r.value]));
        const enabled = (map.get("auto_block_enabled") ?? "true") !== "false";
        const unlockDay = Math.max(
          1,
          Math.min(28, parseInt(map.get("auto_block_unlock_day") ?? "22", 10) || 22),
        );

        if (!enabled || day < unlockDay) {
          if (!cancelled) setShow(false);
          return;
        }

        // Verify next month is actually open (no AUTO_BLOCK rows remaining)
        const { count } = await supabase
          .from("blocked_dates")
          .select("id", { count: "exact", head: true })
          .gte("blocked_date", firstOfNext)
          .lte("blocked_date", lastOfNext)
          .like("reason", "AUTO_BLOCK:%");

        if (cancelled) return;

        if ((count ?? 0) === 0) {
          const monthKey = `${nextMonthYear}-${pad(nextMonth + 1)}`;
          const dismissedKey = localStorage.getItem(DISMISS_KEY);
          if (dismissedKey === monthKey) {
            setShow(false);
          } else {
            setMonthLabel(`${MONTH_NAMES[nextMonth]} ${nextMonthYear}`);
            setDismissKey(monthKey);
            setShow(true);
          }
        } else {
          setShow(false);
        }
      } catch {
        if (!cancelled) setShow(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!show) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleNavigate(); } }}
      className="bg-primary text-primary-foreground border-b border-primary/40 relative cursor-pointer hover:bg-primary/90 transition-colors"
      aria-label={`${monthLabel} bookings are now open. Click to book.`}
    >
      <div className="container mx-auto px-4 py-2.5 pr-10 flex items-center justify-center gap-2 text-center">
        <Sparkles size={14} className="shrink-0" />
        <p className="text-[11px] sm:text-xs tracking-[0.25em] uppercase font-light">
          {monthLabel} bookings are now open — tap to book
        </p>
        <Sparkles size={14} className="shrink-0" />
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-primary-foreground/15 transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default BookingsOpenBanner;
